import NextLink from 'next/link';
import { Link } from '@lululemon/b2b-ecom-pattern-library';
import { CALIFORNIA_PRIVACY_TEXT, CALIFORNIA_TRANSPARENCY_TEXT } from 'helpers/constants/common';
import { useFormat } from 'helpers/hooks/useFormat';
import { isNorthAmericaStore } from 'helpers/utils/countryHelper';
import { FEATURE_FLAG_LIST, useFeatureFlags } from 'hooks';
import { useAccount } from 'frontastic/index';
import styles from '../simple-footer/simple-footer.module.scss';
import { FooterLinkProps, LocaleByPartnerCountry, LocaleSpecific } from '../detailed-footer-int/types';
import { filteredLocaleSpecificFooterData } from '../detailed-footer-int/utils';

export type DataProps = {
  data: {
    linksCol: FooterLinkProps[];
  };
};

type SimpleFooterProps = {
  links: FooterLinkProps[];
};

export const SimpleFooterWrapper = ({ data }: DataProps) => {
  return <SimpleFooter links={data.linksCol} />;
};

const SimpleFooter = ({ links }: SimpleFooterProps) => {
  const { formatMessage } = useFormat({ name: 'common' });
  const { account } = useAccount();
  const { isFeatureEnabled } = useFeatureFlags();

  const activeStore = account?.activeStore;
  const partnerCountry = account?.companyProfile?.partnerCountry;
  const isHideCaliforniaFooterLinkEnabled = isFeatureEnabled(FEATURE_FLAG_LIST.HIDE_CALIFORNIA_FOOTER_LINK_INT);

  const filteredLinks =
    isHideCaliforniaFooterLinkEnabled && activeStore && !isNorthAmericaStore(activeStore)
      ? links.filter(
          (link) =>
            !link.name.toLowerCase().includes(CALIFORNIA_PRIVACY_TEXT) &&
            !link.name.toLowerCase().includes(CALIFORNIA_TRANSPARENCY_TEXT),
        )
      : links;

  const localeValue = partnerCountry
    ? LocaleByPartnerCountry[partnerCountry as keyof typeof LocaleByPartnerCountry]
    : '';

  const filteredLocaleSpecificLinks = filteredLocaleSpecificFooterData(filteredLinks, localeValue as LocaleSpecific);

  return (
    <footer data-testid="simple-footer_test-id" className={styles.simpleFooterContainer}>
      <ul role="list" className={styles.legalLinksContainer}>
        {filteredLocaleSpecificLinks.length &&
          filteredLocaleSpecificLinks.map(({ name, reference }, index) => {
            return (
              <NextLink
                key={name}
                href={reference.type === 'link' ? reference.link : reference.pageFolder._url}
                passHref
                rel={reference.openInNewWindow ? 'noopener' : undefined}
                target={reference.openInNewWindow ? '_blank' : undefined}
              >
                <Link
                  className={styles.legalLink}
                  data-testid={`simple-footer__legal-link-${index}_test-id`}
                  key={`footer-${index}`}
                  tag="li"
                  variant="underlineAnimated"
                >
                  {name}
                </Link>
              </NextLink>
            );
          })}
        <li className={styles.copyright}>
          {formatMessage({
            id: 'copyright',
            defaultMessage: '© lululemon athletica 1818 Cornwall Ave, Vancouver BC V6J 1C7',
          })}
        </li>
      </ul>
    </footer>
  );
};

export default SimpleFooter;
